import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./authPromptModal.module.scss";

interface AuthPromptModalProps {
  reason: "attempt" | "vocab" | "generic";
  onClose: () => void;
}

const AuthPromptModal: React.FC<AuthPromptModalProps> = ({ reason, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = encodeURIComponent(location.pathname + location.search);

  const messages: Record<string, { title: string; description: string }> = {
    attempt: {
      title: t("authPrompt.saveProgress", "Save your progress"),
      description: t("authPrompt.attemptDescription", "Create a free account to save your results and track your learning journey."),
    },
    vocab: {
      title: t("authPrompt.buildVocabulary", "Build your vocabulary"),
      description: t("authPrompt.vocabDescription", "Create a free account to save words to your personal vocabulary and review them later."),
    },
    generic: {
      title: t("authPrompt.createAccount", "Create an account"),
      description: t("authPrompt.genericDescription", "Sign up for free to unlock all features."),
    },
  };

  const { title, description } = messages[reason] || messages.generic;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          <i className="fas fa-times" />
        </button>
        <div className={styles.icon}>
          <i className={`fas fa-${reason === "vocab" ? "book" : reason === "attempt" ? "chart-line" : "user-plus"}`} />
        </div>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.description}>{description}</p>
        <div className={styles.buttons}>
          <button
            className={styles.signupBtn}
            onClick={() => { onClose(); navigate(`/signup?returnTo=${returnPath}`); }}
          >
            {t("signUp", "Sign Up")}
          </button>
          <button
            className={styles.loginBtn}
            onClick={() => { onClose(); navigate(`/login?returnTo=${returnPath}`); }}
          >
            {t("signIn", "Log In")}
          </button>
        </div>
        <button className={styles.laterBtn} onClick={onClose}>
          {t("authPrompt.maybeLater", "Maybe later")}
        </button>
      </div>
    </div>
  );
};

export default AuthPromptModal;
